/**
 * A live task chip — V6-05 §6.
 *
 * The chip is *state*, not an event: it shows where the task is now, and the
 * server edits the same message rather than appending a new one. So this
 * renders from `taskRef.state` and `taskRef.statusName`, not from the event
 * that first created it.
 *
 * A deleted task opens a modal saying so and nothing else. Not a link to a
 * 404, not a dead navigation that leaves somebody on an error page wondering
 * what they broke — one sentence, and they are still in the conversation.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { CheckCircle2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ChatMessageView } from './api';

const STATE_STYLE: Record<string, string> = {
  open: 'bg-[var(--surface-2)]',
  done: 'bg-[var(--accent-soft)] text-[var(--accent)]',
  reassigned: 'bg-[var(--surface-2)] vt-muted',
  deleted: 'bg-[var(--surface-2)] vt-muted line-through',
};

export function TaskChip({ message }: { message: ChatMessageView }) {
  const navigate = useNavigate();
  const [deletedNotice, setDeletedNotice] = useState(false);
  const ref = message.taskRef;
  if (!ref?.taskKey) return null;

  const state = ref.state ?? 'open';
  const isDeleted = state === 'deleted';

  return (
    <>
      <button
        type="button"
        data-testid="chat-task-chip"
        data-task-key={ref.taskKey}
        data-state={state}
        title={isDeleted ? 'This task has been deleted' : `Open ${ref.taskKey}`}
        className={cn(
          'vt-mono inline-flex items-center gap-1 rounded-[4px] px-1.5 py-0.5 text-[11px]',
          STATE_STYLE[state],
          !isDeleted && 'hover:text-[var(--accent)]',
        )}
        onClick={() => {
          // §6 — no dead navigation. The task is gone; say so and stay put.
          if (isDeleted) setDeletedNotice(true);
          else navigate(`/tasks/${ref.taskKey}`);
        }}
      >
        {state === 'done' && <CheckCircle2 className="size-3" />}
        {isDeleted && <Trash2 className="size-3" />}
        {ref.taskKey}
        {ref.statusName && !isDeleted && (
          <span className="vt-muted" data-testid="chip-status">
            · {ref.statusName}
          </span>
        )}
      </button>

      {deletedNotice && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Task deleted"
          data-testid="task-deleted-modal"
          className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4"
          onClick={() => setDeletedNotice(false)}
        >
          <div
            className="vt-surface vt-elevated flex w-[280px] flex-col gap-3 p-4 text-center"
            onClick={(event) => event.stopPropagation()}
          >
            {/* §6 — this sentence, and nothing else. */}
            <p className="text-[13px]">This task has been deleted.</p>
            <Button
              variant="secondary"
              size="sm"
              data-testid="task-deleted-close"
              onClick={() => setDeletedNotice(false)}
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
